import { memo } from 'react';
import { useTranslations } from 'next-intl';
import { DataTestConfig } from '@headless-workspace/config';
import { GlowButton, GlowSearchProductListItem, GlowTitle, PropsWithLocale } from '@headless-workspace/glow-ds';
import { ComponentMemo } from '@headless-workspace/typings';
import { StringHelpers } from '@headless-workspace/utils';
import { SearchProductValue } from '../../../../server';

type SearchSuggestionsProps = PropsWithLocale & {
    suggestedProducts: SearchProductValue[];
    onSearchHandler: () => void;
};

export const SearchSuggestions: ComponentMemo<SearchSuggestionsProps> = memo(
    ({ suggestedProducts, onSearchHandler, locale }) => {
        const t = useTranslations('Header.search');

        if (suggestedProducts.length === 0) {
            return null;
        }

        return (
            <section className={'flex flex-col flex-1 gap-1.5 flex-grow-3'}>
                <GlowTitle text={t('label.products')} fontSize={'title5'} />
                <ul
                    className={'grid grid-cols-1 tablet:grid-cols-2 gap-y-1.5 gap-x-2.5 justify-center items-start'}
                    data-testid={DataTestConfig.testIds.searchProductsBanner}
                >
                    {suggestedProducts.map((product, index) => (
                        <GlowSearchProductListItem
                            {...product}
                            price={StringHelpers.formatPrice({
                                price: product.price,
                                currency: product.currency,
                                locale,
                            })}
                            priceBeforeDiscount={StringHelpers.formatPrice({
                                price: product.priceBeforeDiscount,
                                currency: product.currency,
                                locale,
                                defaultValue: null,
                            })}
                            discountPercentageLabel={
                                product.discountPercentage
                                    ? t('label.discount', {
                                          percentage: product.discountPercentage,
                                      })
                                    : undefined
                            }
                            key={`${product.id}-${index}`}
                            engravingOptionLabel={product.isEngravable ? t('label.engravingOption') : undefined}
                        />
                    ))}
                </ul>
                <GlowButton
                    className={'w-maxContent'}
                    label={t('action.viewAllProducts')}
                    onClick={onSearchHandler}
                    dataTestId={DataTestConfig.testIds.searchShowAllProductsButton}
                />
            </section>
        );
    },
);

SearchSuggestions.displayName = 'SearchSuggestions';
